import $api from "../Api";
import ResponseChecker from "../Middlewares/ResponseChecker";
import { defaultMessages } from "../enums/responseMessages.js";
import Endpoints from "../Endpoints";

/**
 * @param {{
 *   params?: object,
 *   query?: {
 *     search?: string,
 *     regionId?: string,
 *     onlyWithoutRegion?: boolean,
 *     onlyWithoutEmployee?: boolean,
 *     employeeId?: string,
 *     locationId?: string,
 *     isActive?: boolean,
 *     isVerified?: boolean,
 *     isAbroad?: boolean,
 *     tagIds?: string | string[],
 *     customerTagFilterType?: string,
 *     mainPlatformId?: string,
 *     leadPlatformId?: string,
 *     leadCampaignId?: string,
 *     leadAdvertisementId?: string,
 *     workspaceId?: string,
 *     parasutAccountId?: string,
 *     conversionType?: "All"|"Converted"|"Unconverted"|"WaitConverted",
 *     convertedStatus?: "CrmCustomer"|"Demo"|"Customer",
 *     "dateRange.startDate"?: string,
 *     "dateRange.endDate"?: string,
 *     page?: number,
 *     limit?: number
 *   }
 * }} [option]
 */
// Dashboard list drill-down additionally accepts OnlyPool, IncludePool,
// OnlyUntagged, ConversionType, HasEligibleSale, LeadPlatformIds,
// DashboardLeadAdvertisementId and the exclusive dashboard acquisition dates.
export const GetAll = async (option) => {
  const response = await $api.get(Endpoints.CrmCustomer.GetAll(option));
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   fullName: string,
 *   companyName?: string,
 *   phoneNo?: string,
 *   email?: string,
 *   regionId?: string,
 *   employeeId?: string,
 *   locationId?: string,
 *   tagIds?: string[],
 *   isActive?: boolean,
 *   isAbroad?: boolean,
 *   leadPlatformId?: string,
 *   leadCampaignId?: string,
 *   workspaceId?: string,
 *   parasutAccountId?: string
 * }} form
 */
export const Create = async (form) => {
  const response = await $api.post(Endpoints.CrmCustomer.Create(), form);
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{ params: { id: string }, query?: object }} option
 */
export const GetById = async (option) => {
  const response = await $api.get(Endpoints.CrmCustomer.GetById(option));
  return ResponseChecker(defaultMessages, response);
};

/**
 * Onay bekleyen CRM müşterisini doğrular. Endpoint body kabul etmez.
 * @param {{ params: { id: string } }} option
 */
export const Verify = async (option) => {
  const response = await $api.put(Endpoints.CrmCustomer.Verify(option));
  return ResponseChecker(defaultMessages, response);
};

/**
 * Tag listesi bu endpoint ile değiştirilmez; UpdateTag kullanın.
 * @param {{
 *   params: { id: string },
 *   body: {
 *     fullName?: string,
 *     companyName?: string,
 *     phoneNo?: string,
 *     email?: string,
 *     regionId?: string,
 *     employeeId?: string | null,
 *     locationId?: string | null,
 *     isActive?: boolean,
 *     isAbroad?: boolean
 *   }
 * }} option
 */
export const Update = async (option) => {
  const response = await $api.put(
    Endpoints.CrmCustomer.Update({ params: option.params }),
    option.body
  );
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{ params: { id: string }, query?: object }} option
 */
export const Delete = async (option) => {
  const response = await $api.delete(Endpoints.CrmCustomer.Delete(option));
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   params: { id: string },
 *   body: {
 *     tagGroupId: string,
 *     tagId: string,
 *     notePlainText: string,
 *     attachmentIds?: string[]
 *   }
 * }} option
 */
export const UpdateTag = async (option) => {
  const response = await $api.put(
    Endpoints.CrmCustomer.UpdateTag({ params: option.params }),
    option.body
  );
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   params: { id: string },
 *   body: Array<{
 *     leadPlatformId: string,
 *     leadId?: string
 *   }>
 * }} option
 */
export const UpdateLeadInfo = async (option) => {
  const response = await $api.put(
    Endpoints.CrmCustomer.UpdateLeadInfo({ params: option.params }),
    option.body
  );
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   params: { id: string },
 *   body: {
 *     workspaceId: string,
 *     tagIds: string[]
 *   }
 * }} option
 */
export const UpdateWorkspace = async (option) => {
  const response = await $api.put(
    Endpoints.CrmCustomer.UpdateWorkspace({ params: option.params }),
    option.body
  );
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   params: { id: string },
 *   query?: { tagGroupId?: string, page?: number, limit?: number }
 * }} option
 */
export const GetTagTimeline = async (option) => {
  const response = await $api.get(Endpoints.CrmCustomer.GetTagTimeline(option));
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{ params: { id: string } }} option
 */
export const ConvertToCustomer = async (option) => {
  const response = await $api.post(
    Endpoints.CrmCustomer.ConvertToCustomer({ params: option.params })
  );
  return ResponseChecker(defaultMessages, response);
};

/**
 * @param {{
 *   targetCrmCustomerId: string,
 *   sourceCrmCustomerIds: string[]
 * }} form
 */
export const Merge = async (form) => {
  const response = await $api.post(Endpoints.CrmCustomer.Merge(), form);
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ params: { id: string }, query?: { page?: number, limit?: number } }} option */
export const GetNotes = async (option) => {
  const response = await $api.get(Endpoints.CrmCustomer.GetNotes(option));
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ plainText: string }} form @param {{ params: { id: string } }} option */
export const CreateNote = async (form, option) => {
  const response = await $api.post(
    Endpoints.CrmCustomer.CreateNote(option),
    form
  );
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ plainText: string }} form @param {{ params: { id: string, noteId: string } }} option */
export const UpdateNote = async (form, option) => {
  const response = await $api.put(
    Endpoints.CrmCustomer.UpdateNote(option),
    form
  );
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ params: { id: string, noteId: string } }} option */
export const DeleteNote = async (option) => {
  const response = await $api.delete(Endpoints.CrmCustomer.DeleteNote(option));
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ params: { id: string }, query: { Page: number, Limit: number } }} option */
export const GetNotifications = async (option) => {
  const response = await $api.get(
    Endpoints.CrmCustomer.GetNotifications(option)
  );
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ params: { id: string, customerId: string } }} option */
export const MatchCustomer = async (option) => {
  const response = await $api.put(Endpoints.CrmCustomer.MatchCustomer(option));
  return ResponseChecker(defaultMessages, response);
};

/** @param {{ params: { id: string } }} option */
export const UnmatchCustomer = async (option) => {
  const response = await $api.delete(
    Endpoints.CrmCustomer.UnmatchCustomer(option)
  );
  return ResponseChecker(defaultMessages, response);
};

export default {
  GetAll,
  Create,
  GetById,
  Verify,
  Update,
  Delete,
  UpdateTag,
  UpdateLeadInfo,
  UpdateWorkspace,
  GetTagTimeline,
  ConvertToCustomer,
  MatchCustomer,
  UnmatchCustomer,
  Merge,
  GetNotifications,
  GetNotes,
  CreateNote,
  UpdateNote,
  DeleteNote,
};
